import React from 'react';
import {FaTimes} from "react-icons/fa";
import {useEdges, useNodes, useReactFlow} from "@xyflow/react";
import {getLayoutedElements} from "../utils.js";
import {registerLocale} from "react-datepicker";
import {ptBR} from 'date-fns/locale/pt-BR';
import "react-datepicker/dist/react-datepicker.css";
import {useAnalysis} from "../../hooks/use-analysis.js";
import {useEdgeBuilder} from "../../association-network-context.jsx";
import {Types} from "../enums.js";
import projectUtils from "../../../../../../utils/project-utils.js";
import InputDateTime from "../../../../../../components/input/input-datetime.jsx";
import {format} from "date-fns";
import InputForm from "../../../../../../components/input/input-form.jsx";
import Button from "../../../../../../components/button/button.jsx";

const SearchPassages = ({show}) => {
    registerLocale('pt-BR', ptBR);

    const {setPanelOpened, selectedNode, filterPassage, setFilterPassage, setIsLoading, setShowCancelRequestBtn} = useEdgeBuilder()
    const nodes = useNodes();
    const edges = useEdges();
    const {setNodes, setEdges, fitView} = useReactFlow();

    const {runAnalysis} = useAnalysis();

    const handleDate = (item, value) => {
        const dateFormated = value ? format(value, "yyyy-MM-dd HH:mm") : null
        setFilterPassage(prev => ({...prev, [item]: dateFormated}))
    }

    const onChange = ({target: {value, id}}) => setFilterPassage(prev => ({...prev, [id]: value}))

    const init = async () => {
        if (!selectedNode) return;

        try {
            if (!validateDate()) return;

            setIsLoading(true);
            setShowCancelRequestBtn(true);

            const nodeData = selectedNode.data;

            let thresholdFormatted = null;
            if (filterPassage.threshold) {
                const thresholdSplit = filterPassage.threshold.split(":")
                thresholdFormatted = (Number(thresholdSplit[0]) * 60 + Number(thresholdSplit[1])) * 1000
            }

            const passageFilter = {
                ...filterPassage,
                threshold: thresholdFormatted,
            }

            const formData = new FormData()
            formData.append("type", Types.COINCIDENT_PASSAGES);
            formData.append("value", nodeData.plate);
            formData.append("coincidentPassages", JSON.stringify(passageFilter));

            const data = await runAnalysis(formData);
            if (!data) return;

            const baseNode = nodes.find(n => n.id === nodeData.plate || n.id === nodeData.formattedValue);
            if (!baseNode) {
                setPanelOpened(null)
                return;
            }

            const coincidentPassageId = `${Types.COINCIDENT_PASSAGES}-${nodeData.plate}`

            setNodes((prev) => {
                const cleaned = prev.filter(
                    (n) => n.data.coincidentPassageId !== coincidentPassageId
                );

                const newNodes = data.nodes.filter(
                    (d) => !cleaned.some((n) => n.id === d.id)
                );

                const coincidentNode = prev.find(n => n.id === coincidentPassageId);

                const layoutedNodes = getLayoutedElements({
                    newNodes,
                    newEdges: data.edges,
                    fixedNode: coincidentNode || baseNode,
                    existingNodes: cleaned,
                    layoutType: "circle"
                });

                return [...cleaned, ...layoutedNodes];
            });

            setEdges((prev) => {
                const cleaned = prev.filter(
                    (e) => e.source !== coincidentPassageId
                );

                const newEdges = data.edges.filter(
                    (d) => !cleaned.some((e) => e.id === d.id)
                );

                return [...cleaned, ...newEdges];
            });

            setPanelOpened(null);

            await fitView({nodes: [baseNode], padding: 0.5, duration: 800});
        } catch (err) {
            console.error(err);
            projectUtils.swalErrorMessage("", "Erro ao expandir conexões.");
        } finally {
            setIsLoading(false);
        }
    }

    const validateDate = () => {
        const requiredFields = [
            { key: 'startDate', message: 'O campo "Data inicial das passagens" é obrigatório.' },
            { key: 'endDate', message: 'O campo "Data final das passagens" é obrigatório.' },
            { key: 'threshold', message: 'O campo "Minuto: segundo antes e depois das passagens" é obrigatório.' },
            { key: 'minMatches', message: 'O campo "Número mínimo de coincidências" é obrigatório.' },
            { key: 'maxPassages', message: 'O campo "Limite de passagens" é obrigatório.' },
        ];

        for (const { key, message } of requiredFields) {
            if (!filterPassage[key]) {
                projectUtils.swalErrorMessage('', message, 'Ok');
                return false;
            }
        }

        if (filterPassage.startDate && filterPassage.endDate) {
            const dtStart = new Date(filterPassage.startDate);
            const dtEnd = new Date(filterPassage.endDate);

            const diff = dtEnd.getTime() - dtStart.getTime();

            if (diff < 0) {
                projectUtils.swalErrorMessage('', 'A data inicial não pode ser maior que a data final', 'Ok');
                return false;
            }
        }

        return true;
    }

    return (
        <div
            className={`absolute top-0 inset-0 flex items-center justify-center bg-[#33333330] ${show ? "" : "hidden"}`}>
            <div className="bg-white rounded-[5px] w-3/6 relative">
                <div className={"p-2 border-b border-[#ddd]"}>
                    <div onClick={() => setPanelOpened(null)}
                         className={"absolute top-0 right-0 p-[10px] cursor-pointer"}>
                        <FaTimes color={"#a3a3a3"}/>
                    </div>
                    <div className="text-[20px] ">
                        Analisar passagens coincidentes
                    </div>
                </div>
                <div className={"grid grid-cols-2 !gap-4 py-[20px] px-[20px]"}>
                    <div className="form-outline-custom">
                        <InputDateTime
                            dateFormat="LL"
                            id={"startDate"}
                            selected={filterPassage.startDate}
                            label={"Data inicial das passagens *"}
                            onChange={(date) => handleDate("startDate", date)}
                        />
                    </div>
                    <div className="form-outline-custom">
                        <InputDateTime
                            dateFormat="LL"
                            id={"endDate"}
                            selected={filterPassage.endDate}
                            label={"Data final das passagens *"}
                            onChange={(date) => handleDate("endDate", date)}
                        />
                    </div>

                    <div className={"col-span-2"}>
                        <InputForm
                            id={"threshold"}
                            label="Minuto: segundo antes e depois das passagens*"
                            value={filterPassage.threshold}
                            onChange={onChange}
                            onBlur={onChange}
                            mask={'00:00'}
                            inputClass="text-upper"
                        />
                    </div>

                    <div>
                        <InputForm
                            id={"minMatches"}
                            label="Número mínimo de coincidências*"
                            value={filterPassage.minMatches}
                            onChange={onChange}
                            onBlur={onChange}
                            type={"number"}
                            inputClass="text-upper"
                        />
                    </div>

                    <div>
                        <InputForm
                            id={"maxPassages"}
                            label="Limite de passagens*"
                            value={filterPassage.maxPassages}
                            onChange={onChange}
                            onBlur={onChange}
                            type={"number"}
                            inputClass="text-upper"
                        />
                    </div>
                </div>
                <div className={"border-t border-[#ddd] py-[10px] px-[20px]"}>
                    <div className={"flex justify-end"}>
                        <Button className={"float-right"} onClick={init}>Analisar</Button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SearchPassages;